import { apiClient, ApiError } from '@/lib/api/client'
import { config } from '@/lib/config'

/**
 * SOLY-REGIE-001 — why the file exists. Absent means « Installation » (a chantier), which is what
 * every project was before the feature. Values are persisted in English; the UI says
 * « Chantier » / « Intervention ».
 */
export type ProjectNature = 'Installation' | 'Intervention'

export type CreateProjectV2RequestDto = {
  projectTypeId: string
  /** SOLY-REGIE-001 — omitted by the wizard (a chantier), sent by « Nouvelle intervention ». */
  nature?: ProjectNature
  salesOwnerId?: string | null
  allowDuplicate: boolean
  projectName?: string | null
  isDirectSale?: boolean
  vatPercent?: number | null
  customer: {
    mode: number
    customerId?: string | null
    new?: {
      type: number
      name?: string | null
      designation?: string | null
      vatNumber?: string | null
      companyRegistrationNumber?: string | null
      preferredLanguageId?: string | null
      primaryContact?: {
        civility: number
        firstName?: string | null
        lastName?: string | null
        phone?: string | null
        email?: string | null
      }
    }
  }
  jobSite: {
    mode: number
    jobSiteId?: string | null
    new?: {
      label?: string | null
      address?: {
        street?: string | null
        number?: string | null
        box?: string | null
        floor?: string | null
        postalCode?: string | null
        city?: string | null
        country?: string | null
      }
    }
  }
}

export type CreateProjectV2ResponseDto = {
  projectId: string
  customerId: string
  jobSiteId?: string | null
}

export type DuplicateClientDto = {
  customerId: string
  score: number
  reasons: string[]
  type: number
  name: string | null
  vatNumber: string | null
  companyRegistrationNumber: string | null
  preferredLanguageId: string | null
  /** False when every project of this client is in the trash: it is hidden from the client search. */
  hasActiveProject?: boolean
  contacts: Array<{
    id: string
    isPrimary: boolean
    civility: number | null
    firstName: string | null
    lastName: string | null
    phone: string | null
    email: string | null
  }>
}

export type DuplicateDetectionError = {
  error: {
    code: string
    message: string
  }
  duplicates: {
    clients: {
      items: DuplicateClientDto[]
    }
    /**
     * SOLY-REGIE-001 — the endpoint has always returned this (`JobSiteDuplicateMatchDto`), the
     * type just never said so. It matters on the short form: a 409 there is as often an address
     * already known as a look-alike client, and the dialog must say which.
     */
    jobSites?: Array<{ jobSiteId: string; reference: string; label?: string | null }>
    hasAny: boolean
  }
}

export class DuplicateDetectionException extends Error {
  constructor(public data: DuplicateDetectionError) {
    super(data.error.message)
    this.name = 'DuplicateDetectionException'
  }
}

export type ClientSalesOwnerV2Dto = {
  id: string
  fullName?: string | null
  initials?: string | null
} | null

export type ProjectJobSiteDetailsV2Dto = {
  id: string
  reference?: string | null
  address?: string | null
} | null

export type ProjectClientDetailsV2Dto = {
  id: string
  name?: string | null
  hasContactEmail?: boolean
  hasInvoicingAddress?: boolean
  preferredLanguageId?: string | null
} | null

// SOLY-ORDER-002 — SalesOrder lifecycle states as serialized by the BE (SalesOrder.Status).
export type SaleStatus = 'draft' | 'confirmed' | 'cancelled'

export type ProjectPermissionsResponse = {
  allowUpdateProject: boolean
  allowViewQuotes: boolean
  allowCreateQuotes: boolean
  allowAcceptQuotes: boolean
  allowViewInvoices: boolean
  // SOLY-ORDER-002 v2 (GAP-B2): false while the sale is cancelled — invoicing is locked until
  // the sale is sold again (the BE also rejects 409 SALE_CANCELLED).
  allowCreateInvoices: boolean
  // SOLY-ORDER-002 fiche 17 — lifecycle guards (BE-computed). canDeleteProject = never invoiced
  // (strictly stronger); canDeleteSale = confirmed sale AND net invoiced == 0 (a full credit note
  // re-opens it).
  canDeleteProject: boolean
  canDeleteSale: boolean
  // SOLY-ORDER-002 v2 — a cancelled sale can be revived (QuoteAccept permission: re-signing).
  saleTotalWithTax?: number | null
}

export type ProjectDetailsV2Dto = {
  id: string
  name?: string | null
  reference?: string | null
  type?: string | null
  typeLabel?: string | null
  /** SOLY-REGIE-001 — orthogonal to `isDirectSale`, which says HOW it is sold, not why it exists. */
  nature?: ProjectNature
  isDirectSale: boolean
  salesOwner?: ClientSalesOwnerV2Dto
  defaultVat?: number | null
  supplierId?: string | null
  supplierName?: string | null
  modelReference?: string | null
  constructionType?: string | null
  constructionTypeLabel?: string | null
  jobSite?: ProjectJobSiteDetailsV2Dto
  client?: ProjectClientDetailsV2Dto
  permissions?: ProjectPermissionsResponse
  // SOLY-ORDER-002 MS-04 — current SalesOrder status, null if no sale.
  saleStatus?: SaleStatus | null
  preferredLanguageId: string
  createdAt?: string | null
  updatedAt?: string | null
  createdBy: string
}

// SOLY-ORDER-002 MS-04 — a supplier order already sent/confirmed when the sale was cancelled (informational).
export type SupplierOrderWarningDto = {
  supplierName: string
  orderReference?: string | null
}

export type CancelSaleResponseDto = {
  salesOrderId: string
  status: string
  supplierOrderWarnings: SupplierOrderWarningDto[]
}

export type DeletedProjectV2Dto = {
  id: string
  reference?: string | null
  name?: string | null
  clientName?: string | null
  deletedAt?: string | null
}

// ── SOLY-ORDER-002 — impact dry-runs ───────────────────────────────────────

/** An upcoming (not-yet-started) RDV the cancel cascade would cancel. Kind = "TechnicalSurvey" | "Installation" | "Appointment". */
export type UpcomingEventDto = {
  kind: string
  /** Calendar start (SOLY-PLAN-007): the day, « yyyy-MM-dd », and the wall clock « HH:mm » — null for Days. */
  startDate: string
  startTime: string | null
  clientConfirmed: boolean
}

/** Dry-run of the cancel cascade — shown in the cancel modal before the user confirms. */
export type CancelSaleImpactDto = {
  upcomingEvents: UpcomingEventDto[]
  cancellableSupplierLines: number
  sentSupplierOrders: SupplierOrderWarningDto[]
}

export type RemoveQuoteFromSaleResponseDto = {
  salesOrderId: string
  status: string
  /** True when that quote was the last one: the sale itself was deleted. */
  saleEmptied: boolean
  supplierOrderWarnings: SupplierOrderWarningDto[]
}

/**
 * SOLY-ORDER-002 fiche 20 — take one quote out of a multi-quote sale. The quote goes back to
 * « Envoyé » and can be re-accepted later. Removing the last one deletes the sale itself.
 */
export async function removeQuoteFromSaleV2(
  projectId: string,
  quoteId: string,
  reason: string,
): Promise<RemoveQuoteFromSaleResponseDto> {
  return await apiClient.post<RemoveQuoteFromSaleResponseDto>(
    `/api/v2/projects/${projectId}/sale/quotes/${quoteId}/remove`,
    { reason },
  )
}

/** Dry-run for the archive modal: nothing is cancelled by an archive, this says what silently disappears. */
export type ArchiveImpactDto = {
  hasActiveSale: boolean
  upcomingEvents: UpcomingEventDto[]
  cancellableSupplierLines: number
  sentSupplierOrders: SupplierOrderWarningDto[]
}


export async function createProjectV2(body: CreateProjectV2RequestDto): Promise<CreateProjectV2ResponseDto> {
  try {
    return await apiClient.post<CreateProjectV2ResponseDto>('/api/v2/projects', body)
  } catch (error: unknown) {
    // Handle 409 Conflict - Duplicate Detection
    if (error instanceof ApiError) {
      const apiResponse = error.response as { status?: number; data?: { error?: { code?: string } } } | undefined
      const status = apiResponse?.status || error.status
      const errorCode = apiResponse?.data?.error?.code || error.code
      const responseData = apiResponse?.data

      if (status === 409 && errorCode === 'DUPLICATE_DETECTED' && responseData) {
        throw new DuplicateDetectionException(responseData as DuplicateDetectionError)
      }
    }
    throw error
  }
}

export async function getProjectDetailsV2(projectId: string): Promise<ProjectDetailsV2Dto> {
  return await apiClient.get<ProjectDetailsV2Dto>(`/api/v2/projects/${projectId}`)
}

export async function updateProjectNameV2(projectId: string, name: string) {
  return await apiClient.put(`/projects/${projectId}/name`, {
    name,
  })
}

export type UpdateProjectPreferredLanguageResponseV2 = {
  preferredLanguageId: string
}

/**
 * Update the project's document-language snapshot. Rare correction — does not
 * touch the customer's default language.
 */
export async function patchProjectPreferredLanguageV2(
  projectId: string,
  preferredLanguageId: string
): Promise<UpdateProjectPreferredLanguageResponseV2> {
  return await apiClient.patch<UpdateProjectPreferredLanguageResponseV2>(
    `/api/v2/projects/${projectId}/preferred-language`,
    { preferredLanguageId }
  )
}

// ── SOLY-DOC-001 — execution (chantier) documents ──────────────────────────

export type ProjectDocumentDto = {
  id: string
  displayName: string
  fileName: string
  contentType: string
  size: number
  createdByUserId: string
  createdAt: string
  uploaderFirstName?: string | null
  uploaderLastName?: string | null
}

export type ProjectDocumentDownloadDto = {
  fileId: string
  fileName: string
  contentType: string
  url: string
  expiresAt: string
}

export async function getProjectDocuments(projectId: string): Promise<ProjectDocumentDto[]> {
  return await apiClient.get<ProjectDocumentDto[]>(`/api/v2/projects/${projectId}/documents`)
}

/**
 * Multipart upload. We bypass apiClient (which forces application/json) and post FormData with raw
 * fetch so the browser sets the multipart boundary itself; auth is the session cookie.
 */
export async function uploadProjectDocument(projectId: string, file: File): Promise<ProjectDocumentDto> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(`${config.apiBaseUrl}/api/v2/projects/${projectId}/documents`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new ApiError(
      response.status,
      data.error?.code ?? 'UPLOAD_FAILED',
      data.error?.message ?? "Erreur lors de l'envoi du document"
    )
  }
  return (await response.json()) as ProjectDocumentDto
}

export async function renameProjectDocument(projectId: string, documentId: string, displayName: string): Promise<void> {
  await apiClient.patch(`/api/v2/projects/${projectId}/documents/${documentId}`, { displayName })
}

export async function deleteProjectDocument(projectId: string, documentId: string): Promise<void> {
  await apiClient.delete(`/api/v2/projects/${projectId}/documents/${documentId}`)
}

export async function getProjectDocumentDownloadUrl(projectId: string, documentId: string): Promise<ProjectDocumentDownloadDto> {
  return await apiClient.get<ProjectDocumentDownloadDto>(`/api/v2/projects/${projectId}/documents/${documentId}/download`)
}

// ─── SOLY-ORDER-002 — project lifecycle actions ──────────────────────────────

/**
 * Cancel the sale: it is EMPTIED (fiches 18/19), not frozen — accepted quotes are unaccepted (back
 * to `issued`, rendered « Généré ») and can be accepted again, a direct sale keeps nothing. There is
 * no « Réactiver la vente » endpoint any more; do not advertise one to the user.
 * Returns any already-sent supplier orders as a non-blocking warning.
 */
export async function cancelSaleV2(projectId: string, reason: string): Promise<CancelSaleResponseDto> {
  return await apiClient.post<CancelSaleResponseDto>(`/api/v2/projects/${projectId}/sale/cancel`, { reason })
}

/** Dry-run of the cancel cascade — feeds the consent recap in the cancel modal. */
export async function getCancelSaleImpactV2(projectId: string): Promise<CancelSaleImpactDto> {
  return await apiClient.get<CancelSaleImpactDto>(`/api/v2/projects/${projectId}/sale/cancel-impact`)
}


/** Dry-run for the archive modal — what would silently leave the planning views. */
export async function getArchiveImpactV2(projectId: string): Promise<ArchiveImpactDto> {
  return await apiClient.get<ArchiveImpactDto>(`/api/v2/projects/${projectId}/archive-impact`)
}

/** Archive (soft delete) the project. Legacy route (ProjectController has no /api/v2 prefix). */
export async function archiveProjectV2(projectId: string): Promise<void> {
  await apiClient.delete(`/projects/${projectId}`)
}

/** Restore an archived project. Legacy route. */
export async function restoreProjectV2(projectId: string): Promise<void> {
  await apiClient.post(`/projects/${projectId}/restore`, {})
}

/** List archived projects (the « Archivés » view). */
export async function getDeletedProjectsV2(): Promise<DeletedProjectV2Dto[]> {
  return await apiClient.get<DeletedProjectV2Dto[]>('/api/v2/projects/deleted')
}
